import { useCallback } from 'react'

import * as Sentry from '@sentry/react'
import { useSetAtom } from 'jotai'

import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

import { activeVersionAtom } from 'Automations/automationEditor/atoms'
import {
  canGenerateWorkflow,
  hasBlockingValidationResults,
  resolveNodeDefinitions,
  validatePresentation
} from 'Automations/nodeDefinitions'
import { isAutomationPresentation } from 'Automations/nodeDefinitions/guards'
import { ResolvedNodeDefinitions } from 'Automations/nodeDefinitions/types'

/**
 * Whether a version's stored presentation is safe to publish: it must be a
 * valid presentation, pass validation with no blocking errors, and generate a
 * Conductor workflow. This is the last line of defence — the editor gates its
 * publish button on the same checks, but the automation list publishes a
 * version directly, so an invalid version must be rejected here too.
 */
const isPublishable = (
  presentation: unknown,
  definitions: ResolvedNodeDefinitions
): boolean => {
  if (!isAutomationPresentation(presentation)) return false

  try {
    const validationResults = validatePresentation(presentation, definitions)
    if (hasBlockingValidationResults(validationResults)) return false
  } catch {
    return false
  }

  return canGenerateWorkflow(presentation, definitions)
}

/**
 * Activates (publishes) an automation version. Throws on failure so the calling
 * publish button / menu item can surface the error notification; callers own
 * the user-facing messaging.
 */
export const useActivateAutomationVersion = () => {
  const setActiveVersion = useSetAtom(activeVersionAtom)

  return useCallback(
    async ({
      automationId,
      versionId
    }: {
      automationId: string
      versionId: string
    }): Promise<void> => {
      // Load the version we're about to publish and confirm it validates and can
      // be turned into a Conductor workflow before activating. Every activation
      // path runs through here — the editor's publish button and the
      // automation-list publish menu item — so an invalid version can never be
      // published, even when reached from outside the editor.
      const resVersion = await dashboardv99990101Client.getAutomationVersion({
        automationId,
        versionId
      })

      if (!resVersion.data || resVersion.error) {
        throw new Error('Unable to load automation version')
      }

      const definitions = await resolveNodeDefinitions()

      if (!isPublishable(resVersion.data.presentation, definitions)) {
        // Record for us: a version reaching activation that isn't publishable
        // points to a gap in the editor's validation.
        Sentry.captureException(
          new Error('Automation version was not publishable on activate'),
          { extra: { automationId, versionId } }
        )
        throw new Error('Automation version is not publishable')
      }

      const resActivateVersion =
        await dashboardv99990101Client.postActivateAutomationVersion({
          automationId,
          versionId
        })

      if (!resActivateVersion.data || resActivateVersion.error) {
        throw new Error('Unable to activate automation version')
      }

      setActiveVersion(resActivateVersion.data)
    },
    [setActiveVersion]
  )
}
